import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { NAV_LABELS } from '@/lib/vocabulary/securityTerminology';

const statusBadge = (status?: string) => {
  switch ((status || '').toLowerCase()) {
    case 'critical':
      return { label: 'Critical', tone: 'danger' as const };
    case 'warn':
    case 'warning':
      return { label: 'Warning', tone: 'warning' as const };
    case 'resolved':
    case 'ok':
      return { label: 'Resolved', tone: 'success' as const };
    default:
      return { label: 'Info', tone: 'neutral' as const };
  }
};

export default function DomainLogs() {
  const { domain } = useOutletContext<{ domain: { id: string; domain_name: string } }>();
  const [filters, setFilters] = useState({
    category: '',
    owaspId: '',
    status: '',
    controlKey: '',
    enforced: '',
    search: '',
    groupBy: 'none',
    limit: 50,
    offset: 0,
  });

  const logsQuery = useQuery({
    queryKey: ['domain-logs', domain.id, filters],
    queryFn: async () => {
      let query = supabase
        .from('domain_logs')
        .select('id, created_at, category, event_type, severity, summary, owasp_id, control_key, status, enforced, recommended_action', { count: 'exact' })
        .eq('domain_id', domain.id)
        .order('created_at', { ascending: false })
        .range(filters.offset, filters.offset + filters.limit - 1);

      if (filters.category) query = query.eq('category', filters.category);
      if (filters.owaspId) query = query.eq('owasp_id', filters.owaspId);
      if (filters.status) query = query.eq('status', filters.status);
      if (filters.controlKey) query = query.eq('control_key', filters.controlKey);
      if (filters.enforced) query = query.eq('enforced', filters.enforced === 'true');

      const { data, error, count } = await query;
      if (error) throw error;

      const search = filters.search.trim().toLowerCase();
      const filtered = search
        ? (data || []).filter((log) => {
            const haystack = `${log.summary ?? ''} ${log.event_type ?? ''} ${log.control_key ?? ''}`.toLowerCase();
            return haystack.includes(search);
          })
        : data || [];

      return { logs: filtered, count: count ?? 0 };
    },
  });

  const logs = logsQuery.data?.logs || [];
  const total = logsQuery.data?.count || 0;

  const owaspIds = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((log: any) => log.owasp_id && set.add(log.owasp_id));
    return Array.from(set);
  }, [logs]);

  const controlKeys = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((log: any) => log.control_key && set.add(log.control_key));
    return Array.from(set);
  }, [logs]);

  const statuses = useMemo(() => {
    const set = new Set<string>();
    logs.forEach((log: any) => log.status && set.add(log.status));
    return Array.from(set);
  }, [logs]);

  const groupedLogs = useMemo(() => {
    if (filters.groupBy === 'none') return { All: logs };
    const map = new Map<string, any[]>();
    logs.forEach((log: any) => {
      const key = filters.groupBy === 'owasp' ? log.owasp_id || 'Other' : log.control_key || 'Other';
      const list = map.get(key) || [];
      list.push(log);
      map.set(key, list);
    });
    return Object.fromEntries(map.entries());
  }, [filters.groupBy, logs]);

  const categoryLabel = (value?: string | null) => {
    if (!value) return 'Security activity';
    if (value === 'owasp') return 'Protection coverage';
    if (value === 'security_control') return 'Security controls';
    return value;
  };

  const eventTypeLabel = (value?: string | null) => {
    switch (value) {
      case 'admin_geo_observed':
        return 'Admin access observed';
      case 'admin_geo_allowed':
        return 'Admin access allowed';
      case 'admin_geo_blocked':
        return 'Admin access blocked';
      case 'admin_geo_policy_changed':
        return 'Admin access rules updated';
      case 'admin_geo_bypass_issued':
        return 'Emergency bypass issued';
      case 'admin_geo_bypass_used':
        return 'Emergency bypass used';
      default:
        return value || 'Event';
    }
  };

  return (
    <Card className="border-border/60">
      <CardHeader>
        <CardTitle>{NAV_LABELS.securityActivity}</CardTitle>
        <CardDescription>Filter and review protection coverage and security control events for this site.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Input
            placeholder="Search logs"
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            className="w-full md:w-56"
          />
          <select
            value={filters.category}
            onChange={(event) => setFilters((prev) => ({ ...prev, category: event.target.value, offset: 0 }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">All categories</option>
            <option value="owasp">Protection coverage</option>
            <option value="security_control">Security controls</option>
          </select>
          <select
            value={filters.owaspId}
            onChange={(event) => setFilters((prev) => ({ ...prev, owaspId: event.target.value, offset: 0 }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">All coverage mappings</option>
            {owaspIds.map((id) => (
              <option key={id} value={id}>{id}</option>
            ))}
          </select>
          <select
            value={filters.status}
            onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value, offset: 0 }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">All statuses</option>
            {statuses.map((status) => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          <select
            value={filters.controlKey}
            onChange={(event) => setFilters((prev) => ({ ...prev, controlKey: event.target.value, offset: 0 }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">All controls</option>
            <option value="admin_geo_guard">Admin Access Guard</option>
            {controlKeys.map((key) => (
              <option key={key} value={key}>{key}</option>
            ))}
          </select>
          <select
            value={filters.enforced}
            onChange={(event) => setFilters((prev) => ({ ...prev, enforced: event.target.value, offset: 0 }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="">Enforcement (any)</option>
            <option value="true">Enforced</option>
            <option value="false">Not enforced</option>
          </select>
          <select
            value={filters.groupBy}
            onChange={(event) => setFilters((prev) => ({ ...prev, groupBy: event.target.value }))}
            className="px-2 py-1 rounded-md border border-border/50 bg-background/70 text-xs"
          >
            <option value="none">No grouping</option>
            <option value="owasp">Group by coverage mapping</option>
            <option value="control">Group by control</option>
          </select>
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Showing {logs.length} of {total} events</span>
        </div>

        <div className="space-y-4">
          {Object.entries(groupedLogs).map(([group, entries]) => (
            <div key={group} className="space-y-2">
              {filters.groupBy !== 'none' && (
                <div className="text-xs font-semibold text-muted-foreground">{group}</div>
              )}
              {(entries as any[]).length === 0 ? (
                <p className="text-sm text-muted-foreground">No events found.</p>
              ) : (entries as any[]).map((log) => {
                const badge = statusBadge(log.status);
                return (
                  <div key={log.id} className="p-3 rounded border border-border/40 bg-muted/30 text-xs space-y-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono break-all">{log.owasp_id || categoryLabel(log.category)}</span>
                      <span>{new Date(log.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={badge.tone}>{badge.label}</Badge>
                      {log.enforced !== undefined && (
                        <Badge variant={log.enforced ? 'success' : 'neutral'}>
                          {log.enforced ? 'Enforced' : 'Not enforced'}
                        </Badge>
                      )}
                    </div>
                    <div>Control: {log.control_key || 'n/a'}</div>
                    <div>Event: {eventTypeLabel(log.event_type)}</div>
                    <div>Severity: {log.severity}</div>
                    {log.summary && (
                      <div className="text-muted-foreground break-all">{log.summary}</div>
                    )}
                    {log.recommended_action && (
                      <div className="text-muted-foreground">Recommended: {log.recommended_action}</div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilters((prev) => ({ ...prev, offset: Math.max(prev.offset - prev.limit, 0) }))}
            disabled={filters.offset === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setFilters((prev) => ({ ...prev, offset: prev.offset + prev.limit }))}
            disabled={filters.offset + filters.limit >= total}
          >
            Next
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
